/**
 * Renders a single emoji as a Noto Lottie animation, falling back to the plain character.
 */

import { useEffect, useRef, useState } from 'react'
// React 19 removed the global JSX namespace; it now ships from the react package.
import type { JSX } from 'react'
import { DotLottie } from '@lottiefiles/dotlottie-web'

import {
  defaultPlayback,
  loadEmojiAnimation,
  resolveAnimatedCodepoint,
  segmentGraphemes,
  type EmojiPlayback,
} from '../../services/emojiAnimation'

interface AnimatedEmojiProps {
  /** Noto codepoint resolved from the manifest, e.g. "1f3c6". */
  codepoint: string
  /** The original character, used for the accessible name and as fallback. */
  character: string
  /** Playback mode; defaults to a single play. */
  playback?: EmojiPlayback
}

/**
 * Backing-store resolution of the canvas, in pixels.
 *
 * The displayed size follows the inherited font size through CSS (see the host below), so it
 * stays correct across breakpoints without measuring. Only the raster resolution is fixed;
 * beyond this size the animation is upscaled.
 */
const CANVAS_RESOLUTION_PX = 128

/** Emoji glyphs are wider than 1em; matching that keeps surrounding text from shifting. */
const EMOJI_ADVANCE_WIDTH = '1.2em'

export function AnimatedEmoji({
  codepoint,
  character,
  playback = 'once',
}: AnimatedEmojiProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (failed) return

    let player: DotLottie | null = null
    let cancelled = false

    loadEmojiAnimation(codepoint)
      .then((data) => {
        const canvas = canvasRef.current
        if (cancelled || canvas === null) return
        player = new DotLottie({
          canvas,
          data,
          autoplay: true,
          loop: playback === 'loop',
        })
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      player?.destroy()
    }
  }, [codepoint, playback, failed])

  if (failed) {
    return <span>{character}</span>
  }

  return (
    <span
      className="inline-block"
      style={{
        width: EMOJI_ADVANCE_WIDTH,
        height: EMOJI_ADVANCE_WIDTH,
        verticalAlign: '-0.25em',
      }}
    >
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={character}
        width={CANVAS_RESOLUTION_PX}
        height={CANVAS_RESOLUTION_PX}
        style={{ width: '100%', height: '100%' }}
      />
    </span>
  )
}

interface EmojiTextProps {
  /** Arbitrary text that may contain emoji, e.g. a game mode icon coming from the backend. */
  children: string | null | undefined
  /** Playback mode applied to every animated emoji in the text. */
  playback?: EmojiPlayback
}

/** A run of plain text or a single animated emoji, in source order. */
type TextRun = { kind: 'text'; value: string } | { kind: 'emoji'; value: string; codepoint: string }

function splitIntoRuns(text: string): TextRun[] {
  const runs: TextRun[] = []
  for (const grapheme of segmentGraphemes(text)) {
    const codepoint = resolveAnimatedCodepoint(grapheme)
    if (codepoint === null) {
      const last = runs[runs.length - 1]
      if (last !== undefined && last.kind === 'text') {
        last.value += grapheme
      } else {
        runs.push({ kind: 'text', value: grapheme })
      }
      continue
    }
    runs.push({ kind: 'emoji', value: grapheme, codepoint })
  }
  return runs
}

/**
 * Renders text with every manifest-known emoji replaced by its animation.
 *
 * Use this for emoji rendered directly in JSX; game-mode HTML is handled by useAnimatedEmoji.
 */
export function EmojiText({ children, playback }: EmojiTextProps): JSX.Element | null {
  if (children === null || children === undefined || children === '') {
    return null
  }

  return (
    <>
      {splitIntoRuns(children).map((run, index) =>
        run.kind === 'text' ? (
          <span key={`t${index}`}>{run.value}</span>
        ) : (
          <AnimatedEmoji
            key={`e${index}`}
            codepoint={run.codepoint}
            character={run.value}
            playback={playback ?? defaultPlayback(run.codepoint)}
          />
        )
      )}
    </>
  )
}

export default AnimatedEmoji
